import {
  useCallback,
  useState,
} from 'react';

export type UserRole =
  | 'admin'
  | 'lawyer'
  | 'secretary';

export type UserStatus =
  | 'active'
  | 'disabled';

export type ManagedUser = {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  status: UserStatus;
  authVersion: number;
  failedLoginCount: number;
  lockedUntil: string | null;
  lastLoginAt: string | null;
  passwordChangedAt: string;
  createdAt: string;
  updatedAt: string;
};

type UsersResponse = {
  success: boolean;
  users?: ManagedUser[];
  user?: ManagedUser;
  error?: string;
};

async function parseResponse(
  response: Response
) {
  const data =
    (await response.json()) as UsersResponse;

  if (
    !response.ok ||
    !data.success
  ) {
    throw new Error(
      data.error ||
        'Não foi possível concluir a operação.'
    );
  }

  return data;
}

export function useUsers() {
  const [users, setUsers] =
    useState<ManagedUser[]>([]);

  const [loading, setLoading] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const loadUsers =
    useCallback(async () => {
      setLoading(true);

      try {
        const response =
          await fetch(
            '/api/users',
            {
              credentials:
                'same-origin',
            }
          );

        const data =
          await parseResponse(
            response
          );

        setUsers(
          data.users ?? []
        );

        return data.users ?? [];
      } finally {
        setLoading(false);
      }
    }, []);

  const createUser =
    useCallback(
      async (input: {
        email: string;
        name: string;
        role: UserRole;
        password: string;
      }) => {
        setSaving(true);

        try {
          const response =
            await fetch(
              '/api/users',
              {
                method: 'POST',
                credentials:
                  'same-origin',
                headers: {
                  'Content-Type':
                    'application/json',
                },
                body:
                  JSON.stringify(
                    input
                  ),
              }
            );

          const data =
            await parseResponse(
              response
            );

          if (data.user) {
            setUsers(
              (previous) => [
                ...previous,
                data.user!,
              ]
            );
          }

          return data.user;
        } finally {
          setSaving(false);
        }
      },
      []
    );

  const updateUser =
    useCallback(
      async (
        id: string,
        input: Partial<{
          email: string;
          name: string;
          role: UserRole;
          status: UserStatus;
          password: string;
        }>
      ) => {
        setSaving(true);

        try {
          const response =
            await fetch(
              `/api/users/${encodeURIComponent(
                id
              )}`,
              {
                method: 'PUT',
                credentials:
                  'same-origin',
                headers: {
                  'Content-Type':
                    'application/json',
                },
                body:
                  JSON.stringify(
                    input
                  ),
              }
            );

          const data =
            await parseResponse(
              response
            );

          if (data.user) {
            setUsers(
              (previous) =>
                previous.map(
                  (user) =>
                    user.id === id
                      ? data.user!
                      : user
                )
            );
          }

          return data.user;
        } finally {
          setSaving(false);
        }
      },
      []
    );

  return {
    users,
    loading,
    saving,
    loadUsers,
    createUser,
    updateUser,
  };
}
